import React, { useEffect, useState } from 'react';
import { Loader2, Trash2, X } from 'lucide-react';

const ProjectWorkspaceModal = ({
  isOpen,
  project = null,
  onClose,
  onSave,
  onDelete,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [instructions, setInstructions] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setName(project?.name || '');
    setDescription(project?.description || '');
    setInstructions(project?.instructions || '');
    setError('');
    setIsSaving(false);
    setIsDeleting(false);
  }, [isOpen, project]);

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName || isSaving) return;

    setIsSaving(true);
    setError('');
    try {
      await onSave?.({
        projectId: project?.id,
        name: trimmedName,
        description: description.trim(),
        instructions: instructions.trim(),
      });
      onClose?.();
    } catch (saveError) {
      setError(saveError.message || 'Failed to save workspace');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!project?.id || isDeleting) return;
    setIsDeleting(true);
    setError('');
    try {
      await onDelete?.(project.id);
      onClose?.();
    } catch (deleteError) {
      setError(deleteError.message || 'Failed to delete workspace');
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isOpen) return null;

  const isEditing = Boolean(project?.id);

  return (
      <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/45 px-4 backdrop-blur-[2px]">
        <div className="flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border theme-border theme-panel-bg shadow-2xl">
          <div className="flex items-center justify-between gap-3 border-b theme-border px-4 py-3">
            <div>
              <div className="font-semibold theme-text-primary">
                {isEditing ? 'Edit Workspace' : 'New Workspace'}
              </div>
              <div className="text-xs theme-text-muted">Chats and instructions stay grouped here.</div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-lg theme-text-muted hover:bg-[var(--color-sidebar-item-hover)]"
              title="Close"
            >
              <X size={18} />
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            {error && (
              <div className="mb-3 rounded-lg border border-red-200 bg-red-50/80 p-3 text-sm text-red-600 dark:border-red-900 dark:bg-red-950/30 dark:text-red-400">
                {error}
              </div>
            )}

            <label className="mb-3 block">
              <span className="mb-1 block text-xs font-medium theme-text-muted">Name</span>
              <input
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                className="w-full rounded-xl border theme-border theme-card-bg px-3 py-2 text-sm theme-text-primary outline-none focus:ring-2 focus:ring-black/10 dark:focus:ring-white/15"
                placeholder="Product launch"
                maxLength={80}
              />
            </label>

            <label className="mb-3 block">
              <span className="mb-1 block text-xs font-medium theme-text-muted">Description</span>
              <input
                type="text"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                className="w-full rounded-xl border theme-border theme-card-bg px-3 py-2 text-sm theme-text-primary outline-none focus:ring-2 focus:ring-black/10 dark:focus:ring-white/15"
                placeholder="Optional context for this workspace"
                maxLength={500}
              />
            </label>

            <label className="mb-4 block">
              <span className="mb-1 block text-xs font-medium theme-text-muted">Instructions</span>
              <textarea
                value={instructions}
                onChange={(event) => setInstructions(event.target.value)}
                className="min-h-[120px] w-full resize-y rounded-xl border theme-border theme-card-bg px-3 py-2 text-sm leading-6 theme-text-primary outline-none focus:ring-2 focus:ring-black/10 dark:focus:ring-white/15"
                placeholder="How should Arrow behave in this workspace?"
                maxLength={4000}
              />
            </label>

          </div>

          <div className="flex items-center justify-between gap-3 border-t theme-border px-4 py-3">
            {isEditing ? (
              <button
                type="button"
                onClick={handleDelete}
                disabled={isDeleting || isSaving}
                className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-500/10 disabled:opacity-60 dark:text-red-400"
              >
                {isDeleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                Delete
              </button>
            ) : (
              <div />
            )}

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isSaving || isDeleting}
                className="rounded-lg border theme-border theme-button-secondary px-3 py-2 text-sm font-medium disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!name.trim() || isSaving || isDeleting}
                className="inline-flex items-center gap-2 rounded-lg theme-button-primary px-4 py-2 text-sm font-semibold disabled:opacity-60"
              >
                {isSaving && <Loader2 size={16} className="animate-spin" />}
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
  );
};

export default ProjectWorkspaceModal;
